import type { MouseEvent as ReactMouseEvent } from 'react';
import { isElement } from 'react-is';

import type { DataTableCellActionsContent } from './CellActions.js';
import {
  filterActionsForChartColumn,
  getColumnActionsElements,
} from './user-actions-utils.js';
import type {
  Cell,
  Column,
  InitialTableState,
  Row,
  RowData,
  Table,
  TableFeature,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';
import { SortingColumnActions } from '../Sorting/SortingColumnActions.js';

/**
 * Defines the user action table feature
 */
export const UserActions: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      ...state,
      floatingMenuInfo: {
        columnId: null,
        rowId: null,
        x: 0,
        y: 0,
      },
      openMenuRowId: null,
    };
  },

  createTable<TData extends RowData>(table: Table<TData>) {
    table._setFloatingMenuInfo = (
      headerId: string | null,
      columnId: string,
      rowId: string | null,
      x: number,
      y: number,
    ) => {
      table.setState((state) => {
        const newState = { ...state };
        newState.floatingMenuInfo = {
          type: rowId === null ? 'headerAction' : 'cellAction',
          headerId,
          rowId,
          columnId,
          x,
          y,
        };
        newState.openMenuRowId = rowId;
        return newState;
      });
    };

    table.getUserActionsForColumn = (columnId: string) => {
      const column = table.getColumn(columnId);

      if (column?.getCanColumnActions()) {
        return column.getColumnActions();
      }

      return null;
    };

    table.getUserActionsForCell = (columnId: string, rowId: string) => {
      const row = table.getRow(rowId);
      const cell = row
        .getVisibleCells()
        .find((cell) => cell.column.id === columnId);

      if (cell?.column.getCanCellActions()) {
        return cell.getCellActions();
      }
      return null;
    };

    table.getUserActionFloatingMenuContextMenuHandler = () => {
      return (event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (
          event.target instanceof HTMLElement === false &&
          event.target instanceof SVGElement === false
        ) {
          return;
        }

        const cellElement = event.target.closest<HTMLElement>('[role="cell"]');
        // the click was on a body cell
        if (cellElement) {
          const { columnid } = cellElement.dataset;
          const rowid = cellElement
            .closest('[role="row"]')
            ?.getAttribute('data-rowid');

          if (!columnid || !rowid) {
            return;
          }

          // In order to check if the column or cell could take actions we get the actual cell objects.
          const row = table.getRow(rowid);
          const cell = row._getAllCellsByColumnId()[columnid];

          if (cell?.column.getCanCellActions()) {
            table._setFloatingMenuInfo(
              null, // header is not needed for floating menu on body cell
              columnid,
              rowid,
              event.pageX,
              event.pageY,
            );
          }
          return;
        }

        const headerElement = event.target.closest<HTMLElement>(
          '[role="columnheader"]',
        );
        // the click happened on a header cell
        if (headerElement) {
          const { columnid, headerid } = headerElement.dataset;
          if (!columnid) {
            return;
          }

          // In order to check if the column or cell could take actions we get the actual column object.
          const column = table.getColumn(columnid);

          if (column?.getCanColumnActions()) {
            table._setFloatingMenuInfo(
              headerid ?? null,
              columnid,
              null,
              event.pageX,
              event.pageY,
            );
          }
        }
      };
    };

    table.setOpenMenuRowId = (rowId: string | null) => {
      table.setState((state) => ({
        ...state,
        openMenuRowId: rowId,
      }));
    };

    table.getOpenMenuRowId = () => {
      return table.getState().openMenuRowId;
    };
  },

  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getColumnActions = memo(
      () => [
        column.id,
        table.options.columnActions,
        table.options.enableSorting,
      ],
      (columnId, columnActionsOption, enableSortingOption) => {
        const columnActions = getColumnActionsElements(
          columnId,
          columnActionsOption ?? [],
          table.options.consumerColumnDefMap,
        );

        const isChartColumn =
          typeof column.columnDef.enableUserActions === 'object' &&
          column.columnDef.enableUserActions.type === 'chart';
        const filteredActions = isChartColumn
          ? filterActionsForChartColumn(columnActions)
          : columnActions;

        // Append sorting actions at the end when sorting is enabled for this column.
        if (enableSortingOption && column.getCanSort()) {
          filteredActions.push(
            <SortingColumnActions
              key="strato-table-sorting-column-actions"
              hasOtherColumnActions={filteredActions.length > 0}
            />,
          );
        }

        return filteredActions;
      },
      getMemoOptions(table.options, 'debugColumns', 'column.getColumnActions'),
    );

    column.getCanColumnActions = () => {
      if (column.columnDef.enableUserActions === false) {
        return false;
      }

      const columnActionsEnabled = table.options.enableColumnActions;
      const sortingEnabled = table.options.enableSorting && column.getCanSort();

      // the column cannot have any actions if neither column actions nor sorting are enabled for this column.
      if (!columnActionsEnabled && !sortingEnabled) {
        return false;
      }

      const definedColumnActions = column.getColumnActions();
      if (!definedColumnActions) {
        // runtime safety net
        return false;
      }

      return (definedColumnActions.length ?? 0) > 0;
    };

    column.getCellActionsContent = memo(
      () => [column.id, table.options.cellActions],
      (columnId, cellActionsOptions = []) => {
        return cellActionsOptions.reduce((actions, cellAction) => {
          if (
            cellAction.column === undefined ||
            // global action for all columns
            cellAction.column === columnId ||
            // column-specific action
            (typeof cellAction.column === 'function' &&
              cellAction.column(columnId))
          ) {
            actions.push(cellAction.action);
          }
          return actions;
        }, [] as Array<DataTableCellActionsContent<TData>>);
      },
      getMemoOptions(
        table.options,
        'debugCells',
        'column.getCellActionsElements',
      ),
    );

    column.getCanCellActions = () => {
      if (column.columnDef.enableUserActions === false) {
        return false;
      }

      if (!table.options.enableCellActions) {
        return false;
      }

      const definedCellActions = column.getCellActionsContent();
      if (!definedCellActions) {
        // runtime safety net
        return false;
      }

      return (definedCellActions.length ?? 0) > 0;
    };
  },

  createCell<TData extends RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData, TValue>,
    row: Row<TData>,
    table: Table<TData>,
  ) {
    cell.getCellActions = memo(
      () => [column.id, table.options.cellActions],
      () => {
        const cellActions = column.getCellActionsContent().map((cellAction) => {
          // If the entry is a react element, we can directly return it
          if (isElement(cellAction)) {
            return cellAction;
          }
          // If it is a function, we will need to resolve the closure first
          if (typeof cellAction === 'function') {
            const columnDef = table.options.consumerColumnDefMap?.get(
              column.id,
            );
            if (columnDef !== undefined) {
              return cellAction({
                columnDef: columnDef,
                cellValue: cell.getValue(),
                row: row.original,
                rowId: row.id,
              });
            }
          }
          return null;
        });

        return cellActions;
      },
      getMemoOptions(table.options, 'debugCells', 'cell.getCellActions'),
    );
  },
};
